import { forwardRef, Fragment, ReactElement, useMemo, useState } from 'react';
import { Group, Plane, Vector2, Vector2Tuple, Vector3 } from 'three';

import { useCursor } from '@react-three/drei';
import { GroupProps, MeshProps, ThreeEvent, useThree } from '@react-three/fiber';
import { useEventCallback } from '@spazious/react-hooks';
import { useDrag } from '@use-gesture/react';

import { CanvasLabel } from '../CanvasLabel/index.js';
import { Polyline } from '../Polyline/index.js';

const getDistance = (a: Vector2, b: Vector2): number => {
  return a.distanceTo(b);
};

export interface MeasurementControlHandleProps extends MeshProps {
  onDrag?: (payload: { position: Vector2; first: boolean; last: boolean }) => void;
  color?: string;
  size?: number;
  hoverCursor?: string;
  outline?: boolean;
}

export const MeasurementControlHandle: React.FC<MeasurementControlHandleProps> = ({
  onDrag,
  color = 'white',
  hoverCursor = 'move',
  size = 0.1,
  outline,
  ...restProps
}) => {
  const [hover, setHover] = useState(false);
  const { controls, camera, pointer, raycaster } = useThree();
  const bind = useDrag(({ event, down, first, last }) => {
    event.stopPropagation();
    const plane = new Plane(new Vector3(0, 1, 0), 0);
    const pos = new Vector3();
    raycaster.setFromCamera(pointer, camera);
    raycaster.ray.intersectPlane(plane, pos);

    onDrag?.({ position: new Vector2(pos.x, pos.z), first: first, last: last });

    if (controls) {
      (controls as any).enabled = !down;
    }
  });

  useCursor(hover, hoverCursor);

  const handlePointerOver = useEventCallback((evt: ThreeEvent<PointerEvent>) => {
    evt.stopPropagation();
    setHover(true);
  });

  const handlePointerOut = useEventCallback((evt: ThreeEvent<PointerEvent>) => {
    evt.stopPropagation();
    setHover(false);
  });

  return outline ? (
    <group renderOrder={1006}>
      <mesh
        // react-gesture is not infering the EventTarget due always returns ReactDOMAttributes
        {...(bind() as any)}
        {...restProps}
        onPointerOut={handlePointerOut}
        onPointerOver={handlePointerOver}
      >
        <sphereGeometry args={[size, 16, 16]} />
        <meshBasicMaterial color={color} depthTest={false} />
      </mesh>
      <mesh {...(bind() as any)} {...restProps} rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[size, size * 1.6]} />
        <meshBasicMaterial color="orange" depthTest={false} />
      </mesh>
    </group>
  ) : (
    <mesh
      // react-gesture is not infering the EventTarget due always returns ReactDOMAttributes
      {...(bind() as any)}
      {...restProps}
      onPointerOut={handlePointerOut}
      onPointerOver={handlePointerOver}
    >
      <sphereGeometry args={[size, 16, 16]} />
      <meshBasicMaterial color={color} depthTest={false} />
    </mesh>
  );
};

export interface MeasurementControlsProps extends GroupProps {
  points: Vector2Tuple[];
  editable: boolean;
  onChange?: (evt: {
    type: 'remove' | 'add' | 'update';
    first: boolean;
    last: boolean;
    points: Vector2Tuple[];
  }) => void;
  color?: string;
  outline: boolean;
  labelFormatter?: (value: number | string) => string | number;
}

const _va = new Vector2();
const _vb = new Vector2();

export const MeasurementControls: React.FC<MeasurementControlsProps> = forwardRef<
  Group,
  MeasurementControlsProps
>(({ points, onChange, editable, color, labelFormatter, outline }, fowardedRef) => {
  const pointHandles = useMemo(
    () =>
      editable
        ? points.map((point, idx) => (
            <MeasurementControlHandle
              key={`${idx}`}
              outline={outline}
              position={[point[0], 0, point[1]]}
              onDrag={({ position, last, first }) => {
                const newPoints = [...points];
                newPoints[idx] = [position.x, position.y] as Vector2Tuple;
                onChange?.({ type: 'update', last, first, points: newPoints });
              }}
            />
          ))
        : [],
    [editable, points, outline, onChange]
  );

  const labels = useMemo(() => {
    return points.reduce((nodes, point, index, points) => {
      if (index === 0) return nodes;

      const prev = points[index - 1];
      const distance = getDistance(_va.fromArray(point), _vb.fromArray(prev));
      nodes.push(
        <CanvasLabel
          key={`${index}`}
          bgColor="black"
          billboard={true}
          color="white"
          position={new Vector3().lerpVectors(
            new Vector3(point[0], 0, point[1]),
            new Vector3(prev[0], 0, prev[1]),
            0.5
          )}
          rotation={[-Math.PI / 2, 0, 0]}
          size={[1.8, 1]}
        >
          {labelFormatter?.(distance)}
        </CanvasLabel>
      );
      return nodes;
    }, [] as ReactElement<any, any>[]);
  }, [points, labelFormatter]);

  return (
    <group ref={fowardedRef} renderOrder={1006}>
      <Polyline color={color} dashed={false} lineWidth={2} points={points} />
      <Fragment>{labels}</Fragment>

      <group renderOrder={1006}>{pointHandles}</group>
    </group>
  );
});
